import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Circle } from "lucide-react";
import type { SetupChecklistStep, WorkspaceSetupProgress } from "@crewledger/shared/workspace";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function SetupProgressBar({
  completedCount,
  totalSteps,
  progressPercent,
}: {
  completedCount: number;
  totalSteps: number;
  progressPercent: number;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-navy-deep">Setup progress</span>
        <span className="text-muted-foreground">
          {completedCount} of {totalSteps} complete · {progressPercent}%
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  );
}

export function SetupChecklistCard({
  step,
  recommended,
  compact = false,
}: {
  step: SetupChecklistStep;
  recommended: boolean;
  compact?: boolean;
}) {
  const Icon = step.completed ? CheckCircle2 : Circle;

  return (
    <Card
      className={
        recommended
          ? "border-primary/50 shadow-sm ring-1 ring-primary/20"
          : step.completed
            ? "border-green-600/20 bg-green-50/20"
            : undefined
      }
    >
      <CardHeader className={compact ? "pb-2" : "pb-3"}>
        <div className="flex items-start gap-3">
          <Icon
            className={`mt-0.5 h-5 w-5 shrink-0 ${
              step.completed ? "text-green-600" : recommended ? "text-primary" : "text-muted-foreground"
            }`}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className={compact ? "text-sm" : "text-base"}>{step.title}</CardTitle>
              {recommended ? (
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  Recommended next
                </span>
              ) : null}
              {step.completed ? (
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                  Complete
                </span>
              ) : null}
            </div>
            {!compact ? <CardDescription className="mt-1">{step.description}</CardDescription> : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Link to={step.href}>
          <Button
            size="sm"
            variant={step.completed ? "outline" : recommended ? "default" : "secondary"}
          >
            {step.ctaLabel}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export function SetupNextStepBanner({ setup }: { setup: WorkspaceSetupProgress }) {
  const nextStep = setup.steps.find((step) => step.key === setup.nextStepKey);
  if (!nextStep || setup.phase === "complete") return null;

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Next recommended step</CardTitle>
        <CardDescription>
          Start here to keep momentum — each step unlocks the next part of payroll.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-3">
        <p className="text-sm font-medium text-navy-deep">{nextStep.title}</p>
        <Link to={nextStep.href}>
          <Button size="sm">
            {nextStep.ctaLabel}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export function SetupChecklistGrid({
  setup,
  compact = false,
}: {
  setup: WorkspaceSetupProgress;
  compact?: boolean;
}) {
  return (
    <div className={`grid gap-4 ${compact ? "lg:grid-cols-2" : "lg:grid-cols-2"}`}>
      {setup.steps.map((step) => (
        <SetupChecklistCard
          key={step.key}
          step={step}
          recommended={step.key === setup.nextStepKey && !step.completed}
          compact={compact}
        />
      ))}
    </div>
  );
}
